import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbarNotification } from "../../order/reusables/UseSnackBarNotification";
import { addCustomCategory, deleteCategory } from "../../../redux/actions/InventoryAction";

import { CircularProgress, Typography, TextField, Box } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import CancelIcon from "@mui/icons-material/Cancel";

const CategoryManager = () => {
  const dispatch = useDispatch();
  const { showSnackbar, SnackbarComponent } = useSnackbarNotification();
  const { categories, loading: categoriesLoading, error: categoriesError } = useSelector(
    (state) => state.getCategories
  );
  const { userInfo } = useSelector((state) => state.userSignIn);

  const [newCategoryName, setNewCategoryName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const handleAddCategory = (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) {
      showSnackbar("Category name cannot be empty!", "error");
      return;
    }

    dispatch(addCustomCategory(newCategoryName))
      .then(() => {
        showSnackbar("Category added successfully!", "success");
        setNewCategoryName("");
      })
      .catch((error) => {
        showSnackbar("Failed to add category: " + error.message, "error");
      });
  };

  const handleOpenDeleteModal = (category) => {
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const handleDeleteCategory = () => {
    if (categoryToDelete) {
      dispatch(deleteCategory(categoryToDelete.value, userInfo.token))
        .then(() => {
          showSnackbar("Category deleted successfully!", "success");
          handleCloseDeleteModal();
        })
        .catch((error) => {
          showSnackbar("Failed to delete category: " + error.message, "error");
          handleCloseDeleteModal();
        });
    }
  };

  if (categoriesLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (categoriesError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        color="danger"
        className="text-danger"
      >
        <WarningIcon sx={{ mr: 1, color: "red" }} />
        <Typography variant="h6" color="error">
          {categoriesError}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Add New Category Form */}
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <Typography variant="h6" className="mb-3 d-flex align-items-center text-white">
            <AddCircleOutlineIcon sx={{ mr: 1, color: "#1976d2" }} />
            Add New Category
          </Typography>
          <form onSubmit={handleAddCategory}>
            <div className="row g-3 align-items-end">
              <div className="col-md-6 col-12">
                <TextField
                  label="Category Name"
                  variant="outlined"
                  fullWidth
                  placeholder="Enter category name"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  disabled={categoriesLoading}
                  error={!newCategoryName.trim() && newCategoryName.length > 0}
                  helperText={
                    !newCategoryName.trim() && newCategoryName.length > 0
                      ? "Category name cannot be empty"
                      : ""
                  }
                  InputProps={{
                    style: { borderRadius: "8px", color: "white" },
                  }}
                  InputLabelProps={{
                    style: { color: "white" },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "white", 
                      },
                      "&:hover fieldset": {
                        borderColor: "white",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#1976d2", 
                      },
                      "&.Mui-disabled fieldset": {
                        borderColor: "gray",
                      },
                    },
                    "& .MuiFormHelperText-root": {
                      color: "white",
                    },
                  }}
                />
              </div>
              <div className="col-md-6 col-12 d-flex align-items-end">
                <button
                  type="submit"
                  className="btn btn-primary d-flex align-items-center"
                  disabled={categoriesLoading}
                  style={{
                    maxWidth: "200px",
                    padding: "8px 16px",
                  }}
                >
                  <AddCircleOutlineIcon sx={{ mr: 1 }} />
                  {categoriesLoading ? "Adding..." : "Add Category"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Categories Table */}
      <div className="card shadow-sm">
        <div className="card-body">
          <Typography variant="h6" className="mb-3 text-white">
            Categories
          </Typography>
          {categories?.length === 0 ? (
            <Typography variant="body1" color="text.secondary" className="text-center">
              No categories available.
            </Typography>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Category Name</th>
                    <th scope="col">Type</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories?.map((category) => {
                    const isCustom = category.type === "custom";
                    return (
                      <tr key={category.value}>
                        <td className="text-white">{category.name}</td>
                        <td>
                          <span
                            className={`badge ${
                              isCustom ? "bg-success" : "bg-primary"
                            }`}
                          >
                            {isCustom ? "Custom" : "Global"}
                          </span>
                        </td>
                        <td>
                          {isCustom ? (
                            <button
                              className="btn btn-danger btn-sm d-flex align-items-center"
                              onClick={() => handleOpenDeleteModal(category)}
                              disabled={categoriesLoading}
                            >
                              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                              {categoriesLoading ? "Deleting..." : "Delete"}
                            </button>
                          ) : (
                            <span className="text-muted">Cannot Delete</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header border-0">
                <Typography variant="h6" className="modal-title d-flex align-items-center text-white">
                  <WarningIcon sx={{ mr: 1, color: "#d32f2f" }} />
                  Confirm Deletion
                </Typography>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <Typography variant="body1" className="text-white">
                  Are you sure you want to delete the category{" "}
                  <strong>{categoryToDelete?.name}</strong>? This action cannot be undone.
                </Typography>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary d-flex align-items-center"
                  onClick={handleCloseDeleteModal}
                >
                  <CancelIcon sx={{ mr: 1 }} />
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger d-flex align-items-center"
                  onClick={handleDeleteCategory}
                  disabled={categoriesLoading}
                >
                  <DeleteIcon sx={{ mr: 1 }} />
                  {categoriesLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <SnackbarComponent />
    </>
  );
};

export default CategoryManager;